/**
 * 02 · Waterfall EBITDA: de EBITDA BGT a EBITDA real. Cada barra es "mejora /
 * deteriora EBITDA" (no "positivo / negativo") y es el menú de la página: clic
 * abre su detalle en el bloque 03.
 */

import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { PnlWaterfallStep } from '../../../../../shared/finance-pnl';
import { C, type PnlFmt } from './pnl-format';
import { PnlNote, SectionHead } from './pnl-ui';

export type WaterfallKey = PnlWaterfallStep['key'];

const LINE_KEY: Record<Exclude<WaterfallKey, 'start' | 'end'>, string> = {
  revenue: 'budget_control.lines.revenue',
  cogs: 'budget_control.lines.cogs',
  gya: 'budget_control.lines.gya',
  investment: 'budget_control.lines.investment',
  bonos: 'budget_control.lines.bonos',
  taxes: 'budget_control.lines.taxes',
};

export function PnlWaterfall({
  steps,
  selected,
  onSelect,
  periodLabel,
  year,
  splitMonth,
  fmt,
  notes,
}: {
  steps: PnlWaterfallStep[];
  selected: WaterfallKey;
  onSelect: (k: WaterfallKey) => void;
  periodLabel: string;
  year: number;
  splitMonth: number;
  fmt: PnlFmt;
  notes: boolean;
}) {
  const { t } = useTranslation();
  const W = 940;
  const H = 380;
  const L = 8;
  const R = 8;
  const TOP = 36;
  const BOT = 58;
  const slot = (W - L - R) / steps.length;
  const bw = Math.min(64, slot * 0.62);

  let cum = 0;
  const bars = steps.map((s) => {
    if (s.total) {
      if (s.key === 'start') cum = s.value;
      return { s, y0: 0, y1: s.value };
    }
    const b = { s, y0: cum, y1: cum + s.value };
    cum += s.value;
    return b;
  });
  const vals = [0, ...bars.flatMap((b) => [b.y0, b.y1])];
  let mn = Math.min(...vals);
  let mx = Math.max(...vals);
  const pad = (mx - mn) * 0.15 || 1;
  mx += pad;
  mn -= pad * 0.5;
  const Y = (v: number) => TOP + (1 - (v - mn) / (mx - mn)) * (H - TOP - BOT);

  const labelOf = (k: WaterfallKey): string[] => {
    if (k === 'start') return [t('budget_control.waterfall.start'), periodLabel];
    if (k === 'end') return [t('budget_control.waterfall.end'), periodLabel];
    return [t(LINE_KEY[k])];
  };

  return (
    <section>
      <SectionHead num="02" title={t('budget_control.waterfall.title')} sub={t('budget_control.waterfall.subtitle')} />
      <Card className="p-4 shadow-xs">
        <div className="mb-0.5 flex flex-wrap items-center gap-4 text-[11.5px] font-medium text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <i className="block h-[11px] w-[11px] rounded-[3px]" style={{ background: C.neutral }} /> {t('budget_control.waterfall.legend_subtotal')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="block h-[11px] w-[11px] rounded-[3px]" style={{ background: C.fav }} /> {t('budget_control.waterfall.legend_improves')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="block h-[11px] w-[11px] rounded-[3px]" style={{ background: C.unfav }} /> {t('budget_control.waterfall.legend_worsens')}
          </span>
          <span className="ml-auto">{t('budget_control.waterfall.hint')}</span>
          {splitMonth > 0 && (
            <span className="basis-full pt-0.5 text-right">
              {t('budget_control.waterfall.splice', { year, h1: splitMonth - 1, h2: splitMonth })}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <div className="mx-auto min-w-[660px] max-w-[880px]">
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={t('budget_control.waterfall.aria')}>
              {mn < 0 && <line x1={L} x2={W - R} y1={Y(0)} y2={Y(0)} strokeWidth={1} style={{ stroke: C.hairStrong }} />}
              {bars.map((b, i) => {
                const cx = L + slot * i + slot / 2;
                const yA = Y(b.y0);
                const yB = Y(b.y1);
                const top = Math.min(yA, yB);
                const h = Math.max(3, Math.abs(yB - yA));
                const fill = b.s.total ? C.neutral : b.s.value >= 0 ? C.fav : C.unfav;
                const txt = b.s.total ? fmt.k(b.s.value, 0) : fmt.sgnK(b.s.value, 0);
                const above = b.s.value >= 0 || b.s.total;
                const isSel = b.s.key === selected;
                const next = bars[i + 1];
                const ye = next ? (next.s.total ? Y(b.y1) : Y(next.y0)) : null;
                return (
                  <g
                    key={b.s.key}
                    role="button"
                    tabIndex={0}
                    aria-label={`${labelOf(b.s.key).join(' ')}: ${txt}`}
                    aria-pressed={isSel}
                    className={cn('cursor-pointer outline-hidden focus-visible:opacity-80', !isSel && 'hover:opacity-90')}
                    onClick={() => onSelect(b.s.key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(b.s.key);
                      }
                    }}
                  >
                    <rect x={cx - slot / 2} y={TOP - 26} width={slot} height={H - TOP - BOT + 44} fill="transparent" />
                    <rect
                      x={cx - bw / 2}
                      y={top}
                      width={bw}
                      height={h}
                      rx={3}
                      style={{ fill, stroke: isSel ? C.ink : 'none', strokeWidth: isSel ? 1.5 : 0 }}
                    />
                    <text
                      x={cx}
                      y={above ? top - 8 : top + h + 15}
                      textAnchor="middle"
                      fontSize="13"
                      fontWeight="600"
                      className="font-mono tabular-nums"
                      style={{ fill: b.s.total ? C.ink2 : fill }}
                    >
                      {txt}
                    </text>
                    {labelOf(b.s.key).map((l, j) => (
                      <text
                        key={j}
                        x={cx}
                        y={H - BOT + 18 + j * 13}
                        textAnchor="middle"
                        fontSize="11.5"
                        fontWeight={b.s.total ? '600' : '400'}
                        style={{ fill: b.s.total ? C.ink : C.ink2 }}
                      >
                        {l}
                      </text>
                    ))}
                    {ye !== null && next && (
                      <line x1={cx + bw / 2} x2={L + slot * (i + 1) + slot / 2 - bw / 2} y1={ye} y2={ye} strokeWidth={1} strokeDasharray="3 3" style={{ stroke: C.hairStrong }} />
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </Card>
      <PnlNote
        show={notes}
        items={[
          { tag: 'source', title: t('budget_control.notes.waterfall.source_title'), text: t('budget_control.notes.waterfall.source_text') },
          { tag: 'idea', title: t('budget_control.notes.waterfall.idea_title'), text: t('budget_control.notes.waterfall.idea_text') },
          { tag: 'label', title: t('budget_control.notes.waterfall.label_title'), text: t('budget_control.notes.waterfall.label_text') },
          { tag: 'closed', title: t('budget_control.notes.waterfall.closed_title'), text: t('budget_control.notes.waterfall.closed_text') },
        ]}
      />
    </section>
  );
}
